import React, { useMemo, useState } from 'react';
import { Link, Redirect, useHistory } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import { useDashboardQuery } from '../apis/dashboard';
import { useCreateGroupMutation } from '../apis/groups';
import {
  compareParentsByGroupAndName,
  getParentGroupNames,
  hasCompletedFacilitatorProgram,
  hasParentGroup,
  isParentInGroup,
} from '../apis/normalizeDashboardData';
import { hasFullFacilitatorProgramAccessRole } from '../../../utils/authRole';

const getFullName = ({ firstName, lastName }) =>
  [firstName, lastName].filter(Boolean).join(' ').trim();

const isUnnamedFacilitator = (facilitator) =>
  getFullName(facilitator).trim().toLowerCase() === 'unassigned' ||
  !getFullName(facilitator);

const getFacilitatorGroups = (groups, facilitatorId) =>
  groups.filter((group) => group.facilitatorIds.includes(facilitatorId));

const CreateGroupPage = () => {
  const history = useHistory();
  const { data, isLoading, isError } = useDashboardQuery();
  const createGroupMutation = useCreateGroupMutation();
  const isCreatingGroup =
    createGroupMutation.isPending || createGroupMutation.isLoading;

  const [groupName, setGroupName] = useState('');
  const [selectedFacilitatorIds, setSelectedFacilitatorIds] = useState([]);
  const [selectedParentIds, setSelectedParentIds] = useState([]);
  const [parentGroupFilter, setParentGroupFilter] = useState('all');
  const [errors, setErrors] = useState({});

  const namedFacilitators = useMemo(
    () =>
      (data?.facilitators ?? []).filter(
        (facilitator) => !isUnnamedFacilitator(facilitator),
      ),
    [data?.facilitators],
  );
  const assignableFacilitators = useMemo(
    () =>
      namedFacilitators.filter((facilitator) =>
        hasCompletedFacilitatorProgram(facilitator),
      ),
    [namedFacilitators],
  );
  const assignableFacilitatorIds = useMemo(
    () => new Set(assignableFacilitators.map((facilitator) => facilitator.userId)),
    [assignableFacilitators],
  );

  const filteredParents = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.parents
      .filter((parent) => {
        if (parentGroupFilter === 'all') return true;
        if (parentGroupFilter === 'unassigned') return !hasParentGroup(parent);
        return isParentInGroup(parent, parentGroupFilter);
      })
      .sort((firstParent, secondParent) => {
        if (parentGroupFilter === 'all') {
          return compareParentsByGroupAndName(firstParent, secondParent);
        }

        return getFullName(firstParent).localeCompare(
          getFullName(secondParent),
        );
      });
  }, [data, parentGroupFilter]);
  const canCreateGroup = hasFullFacilitatorProgramAccessRole(
    localStorage.getItem('role'),
  );

  const toggleFacilitator = (facilitatorId) => {
    setSelectedFacilitatorIds((prev) =>
      prev.includes(facilitatorId)
        ? prev.filter((id) => id !== facilitatorId)
        : [...prev, facilitatorId],
    );
    setErrors((prev) => ({ ...prev, facilitatorIds: '' }));
  };

  const toggleParent = (parentId) => {
    setSelectedParentIds((prev) =>
      prev.includes(parentId)
        ? prev.filter((id) => id !== parentId)
        : [...prev, parentId],
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedName = groupName.trim();
    const nextErrors = {};
    const validSelectedFacilitatorIds = selectedFacilitatorIds.filter((id) =>
      assignableFacilitatorIds.has(id),
    );

    if (!trimmedName) {
      nextErrors.groupName = 'Group Name is required.';
    }

    if (!validSelectedFacilitatorIds.length) {
      nextErrors.facilitatorIds =
        'Assign at least one facilitator who has completed the program.';
    }

    if (Object.keys(nextErrors).length) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setErrors(nextErrors);
      return;
    }

    try {
      await createGroupMutation.mutateAsync({
        title: trimmedName,
        facilitatorIds: validSelectedFacilitatorIds,
        parentIds: selectedParentIds,
      });
      setErrors({});
      history.push('/facilitator/dashboard');
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        submit: error?.message || 'Unable to create group.',
      }));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (!canCreateGroup) {
    return <Redirect to='/facilitator/dashboard' />;
  }

  if (isLoading) {
    return (
      <div className='text-left'>
        <AdminHeader />
        <main className='p-4 max-w-5xl mx-auto'>
          <div className='my-3'>
            <Link
              className='inline-flex items-center justify-center rounded border border-light-blue bg-white px-3 py-1.5 text-sm font-bold text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
              to='/facilitator/dashboard'
            >
              &larr; Go Back
            </Link>
          </div>
          <section className='rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
            <h1 className='text-xl font-bold text-dark-blue'>
              Loading group data...
            </h1>
          </section>
        </main>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className='text-left'>
        <AdminHeader />
        <main className='p-4 max-w-5xl mx-auto'>
          <div className='my-3'>
            <Link
              className='inline-flex items-center justify-center rounded border border-light-blue bg-white px-3 py-1.5 text-sm font-bold text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
              to='/facilitator/dashboard'
            >
              &larr; Go Back
            </Link>
          </div>
          <section className='rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
            <h1 className='text-xl font-bold text-red-600'>
              Error loading group data.
            </h1>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className='text-left'>
      <AdminHeader />
      <main className='p-4 max-w-5xl mx-auto'>
        <div className='my-3'>
          <Link
            className='inline-flex items-center justify-center rounded border border-light-blue bg-white px-3 py-1.5 text-sm font-bold text-light-blue no-underline transition-colors duration-200 hover:bg-light-blue hover:text-white'
            to='/facilitator/dashboard'
          >
            &larr; Go Back
          </Link>
        </div>

        <h1 className='my-5 text-3xl font-bold text-dark-blue'>
          Create A New Group
        </h1>
        <form onSubmit={handleSubmit} className='flex flex-col gap-5 text-sm'>
          {errors.submit && (
            <section className='rounded-lg border border-red-200 bg-red-50 p-4 shadow-sm'>
              <p className='font-bold text-red-700'>{errors.submit}</p>
            </section>
          )}
          <section className='rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
            <div className='mb-2 text-xs font-bold uppercase tracking-wide text-purple'>
              Step 1
            </div>
            <label
              htmlFor='groupName'
              className='mb-3 block border-l-4 border-purple pl-3 text-xl font-bold text-dark-blue'
            >
              Name Your Group:
            </label>
            <input
              id='groupName'
              type='text'
              value={groupName}
              onChange={(event) => {
                setGroupName(event.target.value);
                setErrors((prev) => ({ ...prev, groupName: '' }));
              }}
              className='w-full max-w-md border border-gray-300 rounded px-3 py-2 text-sm'
            />
            {errors.groupName && (
              <p className='text-red-600 font-bold mt-1'>{errors.groupName}</p>
            )}
          </section>

          <section className='rounded-lg border border-gray-200 bg-slate-50 p-4 shadow-sm'>
            <div className='mb-2 text-xs font-bold uppercase tracking-wide text-purple'>
              Step 2
            </div>
            <h2 className='border-l-4 border-purple pl-3 text-xl font-bold text-dark-blue mb-1'>
              Assign Facilitators to the Group
            </h2>
            <p className='mb-3 max-w-[75ch] text-sm leading-normal'>
              Check the boxes to assign facilitators to the group. Only
              facilitators who have completed the program are shown here.
            </p>
            {errors.facilitatorIds && (
              <p className='mb-3 text-red-600 font-bold'>
                {errors.facilitatorIds}
              </p>
            )}

            <div className='flex max-w-2xl flex-col gap-2 mb-5'>
              {assignableFacilitators.length ? (
                assignableFacilitators.map((facilitator) => {
                  const facilitatorGroups = getFacilitatorGroups(
                    data.groups,
                    facilitator.userId,
                  );

                  return (
                    <label
                      key={facilitator.userId}
                      className='flex items-start gap-2 border border-gray-200 bg-white rounded p-2.5'
                    >
                      <input
                        type='checkbox'
                        className='mt-1 accent-blue-600'
                        checked={selectedFacilitatorIds.includes(
                          facilitator.userId,
                        )}
                        onChange={() => toggleFacilitator(facilitator.userId)}
                      />
                      <span>
                        <span className='block font-bold text-sm'>
                          {getFullName(facilitator)}
                        </span>
                        <span className='block text-xs text-gray-700'>
                          Current Groups:{' '}
                          {facilitatorGroups.length
                            ? facilitatorGroups
                                .map((group) => group.groupName)
                                .join(', ')
                            : 'None'}
                        </span>
                      </span>
                    </label>
                  );
                })
              ) : (
                <p>No facilitators who have completed the program are available.</p>
              )}
            </div>
          </section>

          <section className='rounded-lg border border-gray-200 bg-blue-50/60 p-4 shadow-sm'>
            <div className='mb-2 text-xs font-bold uppercase tracking-wide text-purple'>
              Step 3
            </div>
            <h2 className='border-l-4 border-light-blue pl-3 text-xl font-bold text-dark-blue mb-1'>
              Assign Parents To The Group
            </h2>
            <p className='mb-3 max-w-[75ch] text-sm leading-normal'>
              Parents can only be in one group at a time. Selecting parents who
              are already in a group will override their current group when this
              new group is created.
            </p>

            <label className='block font-bold mb-1' htmlFor='parentGroupFilter'>
              Filter by group
            </label>
            <select
              id='parentGroupFilter'
              value={parentGroupFilter}
              onChange={(event) => setParentGroupFilter(event.target.value)}
              className='border border-gray-300 rounded px-2.5 py-1.5 mb-4 text-sm'
            >
              <option value='all'>All Groups</option>
              <option value='unassigned'>Unassigned</option>
              {data.groups.map((group) => (
                <option key={group.groupId} value={group.groupId}>
                  {group.groupName}
                </option>
              ))}
            </select>

            <div className='grid max-w-3xl grid-cols-[minmax(180px,1fr)_minmax(140px,200px)] gap-3 items-start rounded-t border border-gray-200 bg-white px-3 py-2.5 font-bold'>
              <div>Parent Name</div>
              <div>Current Group</div>
            </div>

            {filteredParents.length ? (
              filteredParents.map((parent) => {
                const parentGroupNames = getParentGroupNames(parent);

                return (
                  <label
                    key={parent.userId}
                    className='grid max-w-3xl grid-cols-[minmax(180px,1fr)_minmax(140px,200px)] gap-3 items-start border-x border-b border-gray-200 bg-white px-3 py-2.5'
                  >
                    <span className='flex items-center gap-2'>
                      <input
                        type='checkbox'
                        className='accent-blue-600'
                        checked={selectedParentIds.includes(parent.userId)}
                        onChange={() => toggleParent(parent.userId)}
                      />
                      <span>
                        <span className='block font-bold'>
                          {getFullName(parent)}
                        </span>
                      </span>
                    </span>
                    <span>
                      {parentGroupNames.length ? (
                        parentGroupNames.join(', ')
                      ) : (
                        <span className='text-red-600 font-bold'>
                          Unassigned
                        </span>
                      )}
                    </span>
                  </label>
                );
              })
            ) : (
              <p className='py-3'>No parents match this filter.</p>
            )}
          </section>

          <div className='flex justify-end'>
            <button
              type='submit'
              className='bg-purple text-white px-5 py-2 rounded font-bold hover:bg-purple-800 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-70'
              disabled={isCreatingGroup}
            >
              {isCreatingGroup ? 'Creating...' : 'Create Group'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CreateGroupPage;
